import React, { useEffect, useMemo, useState } from "react";
import { Icon as LucideIcon } from "lucide-react";
import Modal from "../Modal";
import Button from "../Button";
import Icon from "../Icon";
import { getIconComponent, getIconLabel, getIcon, searchIconGroups } from "../../helpers/lucideIcons";

/**
 * Teto de ícones desenhados de uma vez na aba "Todos". São ~2.000 no total, e
 * montar tudo trava o navegador por segundos — quem não achou nos primeiros
 * busca pelo nome.
 */
const ALL_LIMIT = 120;

const normalize = text => (text ?? '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

export const InputIcon = ({ label, value, node, change, hint }) => {
    const [show, setShow] = useState(false);
    const [term, setTerm] = useState('');
    const [tab, setTab] = useState('curated');
    const [selected, setSelected] = useState(value ?? null);
    const [library, setLibrary] = useState(null);
    const [loadingLibrary, setLoadingLibrary] = useState(false);

    useEffect(() => {
        if(show){
            setSelected(value ?? null);
            setTab(getIcon(value) || !value ? 'curated' : 'all');
        }
    }, [show, value]);

    // A biblioteca inteira é um chunk à parte: só baixa quando o cliente pede.
    useEffect(() => {
        if(tab !== 'all' || library || loadingLibrary) return;

        setLoadingLibrary(true);
        import("../../helpers/lucideAllIcons")
            .then(setLibrary)
            .catch(() => setLibrary(null))
            .then(() => setLoadingLibrary(false));
    }, [tab, library, loadingLibrary]);

    const groups = useMemo(() => searchIconGroups(term), [term]);
    const curatedTotal = groups.reduce((count, group) => count + group.icons.length, 0);

    const allMatches = useMemo(() => {
        if(!library) return [];

        const query = normalize(term).trim();

        if(query === '') return library.ALL_ICON_NAMES;

        return library.ALL_ICON_NAMES.filter(name => normalize(name).includes(query));
    }, [library, term]);

    const labelOf = name => getIcon(name) ? getIconLabel(name) : (library?.getLabel(name) || getIconLabel(name));

    const renderPreview = (name, iconNode, size) => {
        if(getIcon(name)) return React.createElement(getIconComponent(name), { size, strokeWidth: 2 });

        if(library?.getComponent(name)) return React.createElement(library.getComponent(name), { size, strokeWidth: 2 });

        if(Array.isArray(iconNode) && iconNode.length > 0) return <LucideIcon iconNode={iconNode} size={size} strokeWidth={2} />;

        return React.createElement(getIconComponent(name), { size, strokeWidth: 2 });
    }

    const confirm = () => {
        // Ícone do catálogo curado já está compilado nos templates; o de fora
        // viaja com o próprio desenho, senão o site não teria como desenhá-lo.
        change(selected, getIcon(selected) ? null : (library?.getIconNode(selected) ?? null));
        setShow(false);
    }

    return (
        <div className="input-icon">
            {label &&
                <label>{label}</label>
            }
            <button type="button" className="input-icon-trigger" onClick={() => { setTerm(''); setShow(true); }}>
                <span className="input-icon-preview">
                    {renderPreview(value, node, 22)}
                </span>
                <span className="input-icon-texts">
                    <strong>{labelOf(value) || 'Escolher ícone'}</strong>
                    <small>Clique para trocar</small>
                </span>
                <Icon name="edit" size={14} />
            </button>
            {hint &&
                <small className="input-icon-hint">{hint}</small>
            }

            <Modal
                className="modal-input-icon"
                title="Escolher ícone"
                size="big"
                show={show}
                close={() => setShow(false)}
                footer={
                    <>
                        <Button type="secondary" size="small" text="Cancelar" action={() => setShow(false)} />
                        <Button type="primary" size="small" svg="check" text="Usar este ícone" action={confirm} disabled={!selected} />
                    </>
                }
            >
                <div className="icon-picker">
                    {selected &&
                        <div className="icon-picker-selected">
                            <span>{renderPreview(selected, node, 20)}</span>
                            <p>Selecionado: <strong>{labelOf(selected)}</strong></p>
                        </div>
                    }

                    <div className="icon-picker-tabs">
                        <button type="button" className={tab === 'curated' ? 'active' : ''} onClick={() => setTab('curated')}>
                            Sugeridos
                        </button>
                        <button type="button" className={tab === 'all' ? 'active' : ''} onClick={() => setTab('all')}>
                            Todos os ícones
                        </button>
                    </div>

                    <div className="icon-picker-search">
                        <Icon name="search" size={16} />
                        <input
                            type="text"
                            value={term}
                            placeholder={tab === 'curated'
                                ? 'Busque por palavra: frete, orçamento, qualidade, prazo...'
                                : 'Busque pelo nome em inglês: truck, gift, star...'}
                            onChange={e => setTerm(e.target.value)}
                        />
                        {term !== '' &&
                            <button type="button" onClick={() => setTerm('')} aria-label="Limpar busca">
                                <Icon name="close" size={14} />
                            </button>
                        }
                    </div>

                    {tab === 'curated' ?
                        <>
                            <div className="icon-picker-groups">
                                {groups.map(group => (
                                    <div key={group.group} className="icon-picker-group">
                                        <h4>{group.group}</h4>
                                        <div className="icon-picker-grid">
                                            {group.icons.map(icon => {
                                                const Component = icon.component;

                                                return (
                                                    <button
                                                        key={icon.name}
                                                        type="button"
                                                        className={`icon-picker-item ${selected === icon.name ? 'selected' : ''}`}
                                                        onClick={() => setSelected(icon.name)}
                                                        title={icon.label}
                                                    >
                                                        <Component size={22} strokeWidth={2} />
                                                        <span>{icon.label}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {curatedTotal === 0 &&
                                <div className="icon-picker-empty">
                                    Nenhum ícone encontrado para <strong>{term}</strong>. Tente outra palavra, como "entrega" ou "pagamento", ou veja <button type="button" onClick={() => setTab('all')}>todos os ícones</button>.
                                </div>
                            }
                        </>
                    :
                        <div className="icon-picker-all">
                            {loadingLibrary &&
                                <div className="icon-picker-loading">Carregando a biblioteca de ícones...</div>
                            }

                            {library &&
                                <>
                                    <div className="icon-picker-grid">
                                        {allMatches.slice(0, ALL_LIMIT).map(name => {
                                            const Component = library.getComponent(name);

                                            return (
                                                <button
                                                    key={name}
                                                    type="button"
                                                    className={`icon-picker-item ${selected === name ? 'selected' : ''}`}
                                                    onClick={() => setSelected(name)}
                                                    title={library.getLabel(name)}
                                                >
                                                    <Component size={22} strokeWidth={2} />
                                                    <span>{library.getLabel(name)}</span>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {allMatches.length > ALL_LIMIT &&
                                        <div className="icon-picker-more">
                                            Mostrando {ALL_LIMIT} de {allMatches.length} ícones. Busque pelo nome para chegar no que você quer.
                                        </div>
                                    }

                                    {allMatches.length === 0 &&
                                        <div className="icon-picker-empty">
                                            Nenhum ícone com <strong>{term}</strong> no nome. Os nomes são em inglês — tente "box", "phone", "heart".
                                        </div>
                                    }
                                </>
                            }

                            {!library && !loadingLibrary &&
                                <div className="icon-picker-empty">
                                    Não foi possível carregar a biblioteca de ícones. Recarregue a página e tente de novo.
                                </div>
                            }
                        </div>
                    }
                </div>
            </Modal>
        </div>
    );
}

export default InputIcon;
